import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  IconButton,
  alpha,
  useTheme,
  Collapse
} from '@mui/material';
import {
  CelebrationOutlined,
  BrushOutlined,
  Close
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const BANNER_DISMISSED_KEY = 'onboarding_success_banner_dismissed';

const OnboardingSuccessBanner = ({ brandId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Check if banner was already dismissed for this user
    const dismissedKey = `${BANNER_DISMISSED_KEY}_${user?.id}`;
    const wasDismissed = localStorage.getItem(dismissedKey);

    // Show banner if onboarding was just completed and not dismissed
    const onboarding = user?.onboarding;
    const recentlyCompleted = onboarding?.completedAt &&
      (new Date() - new Date(onboarding.completedAt)) < 7 * 24 * 60 * 60 * 1000; // Within 7 days

    // Also show if user has brand with complete plan but hasn't seen the banner
    const hasCompletePlan = user?.hasBrandWithCompletePlan;

    if (!wasDismissed && (recentlyCompleted || hasCompletePlan)) {
      setVisible(true);
    }
  }, [user]);

  const handleDismiss = () => {
    setVisible(false);
    const dismissedKey = `${BANNER_DISMISSED_KEY}_${user?.id}`;
    localStorage.setItem(dismissedKey, 'true');
  };

  const handleGoToDesign = () => {
    if (brandId) {
      navigate(`/brands/${brandId}/design/studio`);
    } else {
      navigate('/design/garment-designer');
    }
  };

  if (!visible) return null;

  return (
    <Collapse in={visible}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
          borderRadius: 2,
          position: 'relative'
        }}
      >
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'text.secondary'
          }}
        >
          <Close fontSize="small" />
        </IconButton>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.success.main, 0.2)
            }}
          >
            <CelebrationOutlined sx={{ fontSize: 28, color: 'success.main' }} />
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" gutterBottom fontWeight={600}>
              You're Ready to Create!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your financial plan is complete. Start designing your first collection with our
              AI-powered design tools, then launch campaigns to bring your vision to life.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              startIcon={<BrushOutlined />}
              onClick={handleGoToDesign}
            >
              Start Designing
            </Button>
          </Box>
        </Box>
      </Paper>
    </Collapse>
  );
};

export default OnboardingSuccessBanner;
